import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TicketDetails from './TicketDetails';
import NavBar from './NavBar';
import ChatbotPopup from './ChatbotPopup';
import './AgentAssist.css';
import wiproLogo from './assets/wipro1.png';
import { FaTicketAlt, FaExclamationTriangle, FaSearch, FaPause, FaPlayCircle, FaFileAlt, FaSort, FaSortUp, FaSortDown, FaDownload, FaCog, FaThLarge, FaExternalLinkAlt, FaChartBar, FaUsers } from 'react-icons/fa';

// Helper function to get status badge style
const getStatusBadgeStyle = (status) => {
  if (!status) return { background: '#f1f5f9', color: '#64748b' };
  const s = status.toLowerCase();
  if (s.includes('in progress') || s.includes('work in progress')) return { background: '#eff6ff', color: '#2563eb' };
  if (s.includes('on hold') || s.includes('pending') || s.includes('hold')) return { background: '#fef3c7', color: '#d97706' };
  if (s.includes('resolved') || s.includes('closed')) return { background: '#ecfdf5', color: '#059669' };
  if (s.includes('new') || s.includes('open')) return { background: '#fef2f2', color: '#dc2626' };
  return { background: '#f1f5f9', color: '#64748b' };
};

// Helper function to get priority badge style
const getPriorityBadgeStyle = (priority) => {
  const label = getPriorityLabel(priority);
  if (!label || label === 'N/A') return { background: '#f1f5f9', color: '#64748b' };
  const l = label.toLowerCase();
  if (l === 'critical') return { background: '#fef2f2', color: '#dc2626', fontWeight: 700 };
  if (l === 'high') return { background: '#fff7ed', color: '#ea580c', fontWeight: 600 };
  if (l === 'moderate' || l === 'medium') return { background: '#fef9c3', color: '#ca8a04', fontWeight: 500 };
  if (l === 'low') return { background: '#ecfdf5', color: '#059669', fontWeight: 500 };
  return { background: '#f1f5f9', color: '#64748b' };
};

// App version - increment this to force cache clear on all clients
const APP_VERSION = '1.3.0'; // Updated to clear cache for priority fix
const NO_GROUP_PREFILTER_TOKEN = '__NO_GROUP_PREFILTER__';
const PREFILTER_BYPASS_EMAILS = new Set([
  'admin@example.com',
  'deepak.mittal@contractor.tranetechnologies.com',
  'mukesh.panwar@contractor.tranetechnologies.com',
  'vipul.narayan.singh@contractor.tranetechnologies.com',
  'harishchandra.karnatak@contractor.tranetechnologies.com',
  'brian.beirne@contractor.tranetechnologies.com',
  'craig.friell@contractor.tranetechnologies.com',
  'mark.hainsworth@contractor.tranetechnologies.com',
  'mukesh.kumar@contractor.tranetechnologies.com',
  'nikita.kapadia@contractor.tranetechnologies.com',
  'rajesh.araveeti@contractor.tranetechnologies.com',
  'ravikumar.n@contractor.tranetechnologies.com',
  'rewanth.reddy@contractor.tranetechnologies.com',
  'shabeer.shaik@contractor.tranetechnologies.com',
  'sri.chitti.durga.rao@contractor.tranetechnologies.com',
  'swapna.mekala@contractor.tranetechnologies.com',
  'vikram.kumar@contractor.tranetechnologies.com'
]);

const fetchData = async (ticketType) => {
  const API_URL = `/api/tickets?type=${ticketType}`
  const response = await fetch(API_URL, { method: "GET" })
  return response
}

const isTicketNumberForType = (number, type) => String(number || '').toUpperCase().startsWith(type === 'service' ? 'SCTASK' : 'INC');

// Priority mapping: handles both numeric (1,2,3,4) and string formats ("1 - Critical")
// ServiceNow priority: 1=Critical (highest), 2=High, 3=Moderate, 4=Low (lowest)
const getPriorityLabel = (priority) => {
  if (!priority) return 'N/A';
  
  // If already a formatted string from API (e.g., "1 - Critical"), extract the text part
  if (typeof priority === 'string' && priority.includes(' - ')) {
    return priority.split(' - ')[1]; // Returns "Critical", "High", "Moderate", "Low"
  }
  
  // If numeric, map to label (ServiceNow standard)
  const mapping = {
    '1': 'Critical',
    '2': 'High',
    '3': 'Moderate',
    '4': 'Low'
  };
  return mapping[String(priority)] || priority;
};

// Extract numeric priority for sorting
// Lower number = higher priority in ServiceNow (1=Critical is most urgent)
const getPriorityNumber = (priority) => {
  if (!priority) return 0;
  
  // If string format "1 - Critical", extract the number
  if (typeof priority === 'string' && priority.includes(' - ')) {
    return parseInt(priority.split(' - ')[0]);
  }
  
  // If already numeric or string number
  return parseInt(priority) || 0;
};

const AgentAssist = ({ userName, userEmail, onLogout, canViewUserAnalytics = false }) => {
  const [filters, setFilters] = useState({
    ticketId: [],
    assignmentGroup: [],
    assignedTo: [],
    status: [],
    shortDescription: [],
    priority: [],
    dateRange: { start: '', end: '' }
  });

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const ticketType = 'incident';
  const ticketRequestRef = useRef(0);
  const ticketCacheKey = ticketType === 'service' ? 'agentAssistServiceTicketsCache' : 'agentAssistTicketsCache';
  const [showDateRange, setShowDateRange] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showChatbot, setShowChatbot] = useState(false);
  
  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    ticketId: true,
    shortDescription: true,
    assignmentGroup: true,
    receivedOn: true,
    assignedTo: true,
    status: true,
    priority: true,
    category: true,
    action: true
  });
  const [showConfigureDropdown, setShowConfigureDropdown] = useState(false);
  const configureDropdownRef = useRef(null);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [groupSearchValue, setGroupSearchValue] = useState('');
  const groupDropdownRef = useRef(null);

  // Timezone state
  const [selectedTimezone, setSelectedTimezone] = useState('Asia/Kolkata');
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const timezoneDropdownRef = useRef(null);
  const [userAssignmentGroups, setUserAssignmentGroups] = useState([]);
  const [prefilterLoading, setPrefilterLoading] = useState(false);
  const [prefilterResolved, setPrefilterResolved] = useState(false);

  const timezoneOptions = [
    { label: 'IST', value: 'Asia/Kolkata', desc: 'India Standard Time (UTC+5:30)' },
    { label: 'UTC', value: 'UTC', desc: 'Coordinated Universal Time' },
    { label: 'EST', value: 'America/New_York', desc: 'Eastern Time (UTC-5)' },
    { label: 'CST', value: 'America/Chicago', desc: 'Central Time (UTC-6)' },
    { label: 'MST', value: 'America/Denver', desc: 'Mountain Time (UTC-7)' },
    { label: 'PST', value: 'America/Los_Angeles', desc: 'Pacific Time (UTC-8)' },
    { label: 'GMT', value: 'Europe/London', desc: 'Greenwich Mean Time' },
    { label: 'CET', value: 'Europe/Berlin', desc: 'Central European Time (UTC+1)' },
    { label: 'JST', value: 'Asia/Tokyo', desc: 'Japan Standard Time (UTC+9)' },
    { label: 'AEST', value: 'Australia/Sydney', desc: 'Australian Eastern Time (UTC+10)' },
  ];

  const getTimezoneLabel = () => {
    const tz = timezoneOptions.find(t => t.value === selectedTimezone);
    return tz ? tz.label : 'IST';
  };

  // Dropdown states for custom filters
  // Group filter now uses native select UI for consistent behavior
  
  const navigate = useNavigate();
  const normalizedUserEmail = (userEmail || '').trim().toLowerCase();
  const bypassPrefilter = PREFILTER_BYPASS_EMAILS.has(normalizedUserEmail);

  useEffect(() => {
    if (!normalizedUserEmail || bypassPrefilter) {
      setUserAssignmentGroups([]);
      setPrefilterResolved(true);
      return;
    }

    const fetchUserAssignmentGroups = async () => {
      setPrefilterLoading(true);
      setPrefilterResolved(false);
      try {
        const response = await fetch(`/api/user-assignment-groups?email=${encodeURIComponent(normalizedUserEmail)}`);
        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result?.error || 'Failed to load user assignment groups');
        }

        const groups = Array.isArray(result.assignmentGroups) ? result.assignmentGroups : [];
        setUserAssignmentGroups(groups);
      } catch (error) {
        console.error('Error loading user assignment groups:', error);
        setUserAssignmentGroups([]);
      } finally {
        setPrefilterLoading(false);
        setPrefilterResolved(true);
      }
    };

    fetchUserAssignmentGroups();
  }, [normalizedUserEmail, bypassPrefilter]);

  useEffect(() => {
    if (!prefilterResolved && !bypassPrefilter) return;
    if (bypassPrefilter) return;

    setFilters((prev) => {
      const nextGroupFilter = userAssignmentGroups.length > 0
        ? userAssignmentGroups
        : [NO_GROUP_PREFILTER_TOKEN];

      const next = {
        ...prev,
        assignmentGroup: nextGroupFilter
      };
      localStorage.setItem('agentAssistFilters', JSON.stringify(next));
      return next;
    });
  }, [userAssignmentGroups, bypassPrefilter, prefilterResolved]);

  // Load saved timezone from localStorage
  useEffect(() => {
    const savedTz = localStorage.getItem('agentAssistTimezone');
    if (savedTz) setSelectedTimezone(savedTz);
  }, []);

  // Load saved column visibility from localStorage
  useEffect(() => {
    const savedColumns = localStorage.getItem('agentAssistColumnVisibility');
    if (savedColumns) {
      try { setColumnVisibility(JSON.parse(savedColumns)); } catch (e) {}
    }
  }, []);

  // Close configure dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (configureDropdownRef.current && !configureDropdownRef.current.contains(event.target)) {
        setShowConfigureDropdown(false);
      }
      if (timezoneDropdownRef.current && !timezoneDropdownRef.current.contains(event.target)) {
        setShowTimezoneDropdown(false);
      }
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedVisibleGroups = filters.assignmentGroup.filter((group) => group !== NO_GROUP_PREFILTER_TOKEN);

  const toggleColumnVisibility = (column) => {
    const updated = { ...columnVisibility, [column]: !columnVisibility[column] };
    // Ensure at least 2 columns remain visible
    const visibleCount = Object.values(updated).filter(Boolean).length;
    if (visibleCount < 2) return;
    setColumnVisibility(updated);
    localStorage.setItem('agentAssistColumnVisibility', JSON.stringify(updated));
  };

  const columnLabels = {
    ticketId: 'Ticket ID#',
    shortDescription: 'Short Description',
    assignmentGroup: 'Assignment Group',
    receivedOn: 'Received On',
    assignedTo: 'Assigned To',
    status: 'State',
    priority: 'Priority',
    category: 'Category',
    action: 'Action'
  };

  // Load saved filters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('agentAssistFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(parsed);
        console.log('Loaded saved filters:', parsed);
      } catch (error) {
        console.error('Error loading saved filters:', error);
      }
    }
  }, []);

  // Load the selected ticket type from its own cache or ServiceNow.
  useEffect(() => {
    ticketRequestRef.current += 1;
    setData([]);
    setError(null);
    setSelectedTicket(null);

    // Check app version and clear cache if version changed
    const cachedVersion = localStorage.getItem('appVersion');
    if (cachedVersion !== APP_VERSION) {
      console.log('App version changed, clearing cache...');
      localStorage.removeItem('agentAssistTicketsCache');
      localStorage.removeItem('agentAssistServiceTicketsCache');
      localStorage.removeItem('agentAssistFilters');
      try {
        localStorage.setItem('appVersion', APP_VERSION);
      } catch (storageError) {
        console.warn('Unable to store app version in browser storage.', storageError);
      }
      // Force fetch fresh data
      handleSyncData(ticketType);
      return;
    }
    
    const cachedData = localStorage.getItem(ticketCacheKey);
    if (cachedData) {
      try {
        const cache = JSON.parse(cachedData);
        if (ticketType === 'service' && (cache.stateFilter !== '-5,1,2' || cache.completeList !== true || cache.groupScope !== 'wipro')) {
          throw new Error('Service ticket cache requires the current state and Wipro group filters.');
        }
        // Check if cache is newer than 20 minutes (1200000 ms)
        const cacheAge = Date.now() - (cache.timestamp || 0);
        if (cacheAge > 1200000) {
          console.log('Cache expired (>20min), fetching fresh data...');
          handleSyncData(ticketType);
        } else {
          // Use cached data if it has tickets array, otherwise use it directly
          const tickets = cache.tickets || cache;
          if (!Array.isArray(tickets) || !tickets.every((ticket) => isTicketNumberForType(ticket.ticketId, ticketType))) {
            throw new Error('Cached tickets do not match the selected ticket type.');
          }
          setData(tickets);
          setLoading(false);
          console.log('Loaded cached tickets:', tickets.length);
        }
      } catch (error) {
        console.error('Error loading cached tickets:', error);
        localStorage.removeItem(ticketCacheKey);
        // If cache is corrupted, fetch fresh data
        handleSyncData(ticketType);
      }
    } else {
      // No cache, fetch data on first load
      handleSyncData(ticketType);
    }
  }, [ticketType]);

  // Function to fetch fresh data from API
  const handleSyncData = async (requestedType = ticketType) => {
    const requestId = ++ticketRequestRef.current;
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchData(requestedType);
      console.log('Raw response:', response);
      const result = await response.json();
      if (requestId !== ticketRequestRef.current) return;
      if (!response.ok) throw new Error(result?.error || `Unable to load ${requestedType} tickets.`);
      if (!Array.isArray(result.result)) throw new Error('Invalid ticket response from ServiceNow.');
      if (!result.result.every((item) => isTicketNumberForType(item.number, requestedType))) {
        throw new Error('The backend returned the wrong ticket type. Restart the Express backend and try again.');
      }
      
      const tickets = (result.result || []).map(item => {
        return {
          sysId: item.sys_id,
          ticketType: requestedType,
          ticketId: item.number,
          shortDescription: item.short_description,
          assignmentGroup: item.assignment_group,
          receivedOn: item.opened_at,
          assignedTo: item.assigned_to || '',
          status: item.state,
          item: item.cmdb_ci || item['request_item.cat_item'] || '',
          category: item.category || item['request_item.cat_item'] || '',
          priority: item.priority, // Use raw ServiceNow value (e.g., "1 - Critical")
          action: 'Review',
          // Keep raw field names for ScreenTimeAnalytics compatibility
          number: item.number,
          short_description: item.short_description,
          assignment_group: item.assignment_group,
          opened_at: item.opened_at,
          assigned_to: item.assigned_to || '',
          resolved_at: item.resolved_at,
          updated_at: item.updated_at,
          state: item.state,
          cmdb_ci: item.cmdb_ci
        };
      });
      console.log('Number of tickets:', tickets.length);
      setData(tickets);
      
      // Cache the data with timestamp
      const cacheData = {
        tickets: tickets,
        timestamp: Date.now(),
        stateFilter: requestedType === 'service' ? '-5,1,2' : undefined,
        completeList: requestedType === 'service' ? true : undefined,
        groupScope: requestedType === 'service' ? 'wipro' : undefined
      };
      try {
        localStorage.setItem(requestedType === 'service' ? 'agentAssistServiceTicketsCache' : 'agentAssistTicketsCache', JSON.stringify(cacheData));
        console.log('Tickets cached successfully with timestamp');
      } catch (cacheError) {
        console.warn('Ticket cache unavailable; displaying fetched tickets without updating the cache.', cacheError);
      }
    } catch (error) {
      if (requestId !== ticketRequestRef.current) return;
      console.error('Error fetching tickets:', error);
      setError(error.message || String(error));
    } finally {
      if (requestId === ticketRequestRef.current) setLoading(false);
    }
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      ticketId: [],
      assignmentGroup: [],
      assignedTo: [],
      status: [],
      shortDescription: [],
      priority: []
    };
    setFilters(emptyFilters);
    setSearchTerm('');
    localStorage.removeItem('agentAssistFilters');
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleCloseDetails = () => {
    setSelectedTicket(null);
  };

  const handleExportToExcel = () => {
    // Use filteredData (already filtered by current filters and search)
    const dataToExport = filteredData.map(ticket => ({
      'Ticket ID': ticket.ticketId,
      'Short Description': ticket.shortDescription,
      'Assignment Group': ticket.assignmentGroup,
      'Received On': ticket.receivedOn,
      'Assigned To': ticket.assignedTo,
      'State': ticket.status,
      'Priority': ticket.priority,
      'Category': ticket.category || ''
    }));

    // Convert to CSV format
    const headers = Object.keys(dataToExport[0] || {});
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row => 
        headers.map(header => {
          const value = String(row[header] || '');
          // Escape quotes and wrap in quotes if contains comma
          return value.includes(',') || value.includes('"') 
            ? `"${value.replace(/"/g, '""')}"` 
            : value;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `tickets_export_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredData = data.filter(agent => {
    // Apply dropdown filters
    const hasSingleGroupFilter = filters.assignmentGroup.length === 1 && filters.assignmentGroup[0] !== NO_GROUP_PREFILTER_TOKEN;
    const normalizedGroupFilter = hasSingleGroupFilter ? filters.assignmentGroup[0].toLowerCase() : '';
    const matchesFilters = (
      (filters.ticketId.length === 0 || filters.ticketId.includes(agent.ticketId)) &&
      (filters.assignmentGroup.length === 0 ||
        (hasSingleGroupFilter
          ? (agent.assignmentGroup || '').toLowerCase().includes(normalizedGroupFilter)
          : filters.assignmentGroup.includes(agent.assignmentGroup))) &&
      (filters.assignedTo.length === 0 || filters.assignedTo.includes(agent.assignedTo)) &&
      (filters.status.length === 0 || filters.status.includes(agent.status)) &&
      (filters.shortDescription.length === 0 || filters.shortDescription.some(desc => agent.shortDescription?.includes(desc))) &&
      (filters.priority.length === 0 || filters.priority.includes(agent.priority))
    );

    // Apply date range filter
    const matchesDateRange = (() => {
      if (!filters.dateRange.start && !filters.dateRange.end) return true;
      if (!agent.receivedOn) return false;
      
      const ticketDate = new Date(agent.receivedOn);
      const startDate = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
      const endDate = filters.dateRange.end ? new Date(filters.dateRange.end) : null;
      
      if (startDate && ticketDate < startDate) return false;
      if (endDate && ticketDate > endDate) return false;
      return true;
    })();

    // Apply search term
    const matchesSearch = searchTerm === '' || 
      agent.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.assignmentGroup?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.assignedTo?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilters && matchesSearch && matchesDateRange;
  });

  // Sorting logic
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Special handling for priority - use numeric value
    if (sortConfig.key === 'priority') {
      aValue = getPriorityNumber(a.priority);
      bValue = getPriorityNumber(b.priority);
    }
    
    // Special handling for date fields (receivedOn)
    if (sortConfig.key === 'receivedOn') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    // String comparison (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTickets = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when filters or search changes (but NOT when sorting)
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, ticketType]); // sortConfig intentionally excluded

  // Ensure current page is valid when total pages changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate statistics based on filtered data
  const stats = {
    total: filteredData.length,
    inProgress: filteredData.filter(t => t.status?.toLowerCase().includes('in progress') || t.status?.toLowerCase().includes('work in progress')).length,
    onHold: filteredData.filter(t => t.status?.toLowerCase().includes('on hold') || t.status?.toLowerCase().includes('hold') || t.status?.toLowerCase().includes('pending')).length,
    new: filteredData.filter(t => (t.status?.toLowerCase().includes('new') || t.status?.toLowerCase().includes('open')) && !t.status?.toLowerCase().includes('in progress')).length
  };

  // Handle stat card clicks to filter by status
  const handleStatClick = (statusType) => {
    let statusValues = [];
    
    switch(statusType) {
      case 'inProgress':
        // Find all unique status values that match "in progress"
        statusValues = [...new Set(data
          .filter(t => t.status?.toLowerCase().includes('in progress') || t.status?.toLowerCase().includes('work in progress'))
          .map(t => t.status))];
        break;
      case 'onHold':
        // Find all unique status values that match "on hold"
        statusValues = [...new Set(data
          .filter(t => t.status?.toLowerCase().includes('on hold') || t.status?.toLowerCase().includes('hold') || t.status?.toLowerCase().includes('pending'))
          .map(t => t.status))];
        break;
      case 'new':
        // Find all unique status values that match "new"
        statusValues = [...new Set(data
          .filter(t => (t.status?.toLowerCase().includes('new') || t.status?.toLowerCase().includes('open')) && !t.status?.toLowerCase().includes('in progress'))
          .map(t => t.status))];
        break;
      case 'total':
        // Clear status filter to show all
        statusValues = [];
        break;
    }
    
    const newFilters = {
      ...filters,
      status: statusValues
    };
    setFilters(newFilters);
    localStorage.setItem('agentAssistFilters', JSON.stringify(newFilters));
  };

  const getOptions = (key) => {
    if (!Array.isArray(data)) return [];
    const values = data.map(agent => agent[key]).filter(val => typeof val !== 'undefined' && val !== null);
    const unique = [...new Set(values)];
    return unique.map(option => ({ value: option, label: option }));
  };

  const groupOptions = getOptions('assignmentGroup');
  const filteredGroupOptions = groupOptions.filter((option) =>
    option.label?.toLowerCase().includes(groupSearchValue.toLowerCase())
  );

  const handleStatusSelectChange = (event) => {
    const selectedStatus = event.target.value;
    const newFilters = {
      ...filters,
      status: selectedStatus === 'all' ? [] : [selectedStatus]
    };
    setFilters(newFilters);
    localStorage.setItem('agentAssistFilters', JSON.stringify(newFilters));
  };

  const handlePrioritySelectChange = (event) => {
    const selectedPriority = event.target.value;
    const newFilters = {
      ...filters,
      priority: selectedPriority === 'all' ? [] : [selectedPriority]
    };
    setFilters(newFilters);
    localStorage.setItem('agentAssistFilters', JSON.stringify(newFilters));
  };

  const handleGroupOptionToggle = (value) => {
    const currentlySelected = filters.assignmentGroup.includes(value);
    const nextSelected = currentlySelected
      ? filters.assignmentGroup.filter((group) => group !== value)
      : [...filters.assignmentGroup.filter((group) => group !== NO_GROUP_PREFILTER_TOKEN), value];

    const newFilters = {
      ...filters,
      assignmentGroup: nextSelected
    };
    setFilters(newFilters);
    localStorage.setItem('agentAssistFilters', JSON.stringify(newFilters));
  };

  const handleClearGroupFilter = () => {
    const newFilters = {
      ...filters,
      assignmentGroup: []
    };
    setFilters(newFilters);
    localStorage.setItem('agentAssistFilters', JSON.stringify(newFilters));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    // Note: currentPage is NOT reset when sorting - user stays on same page number
  };

  const handleChatbotToggle = () => {
    if (!showChatbot) {
      setShowChatbot(true);
      return;
    }
    setShowChatbot(false);
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading tickets...</p>
      <p style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '-8px' }}>Fetching data from ServiceNow</p>
    </div>
  );

  if (prefilterLoading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Applying assignment group prefilter...</p>
      <p style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '-8px' }}>Loading your assignment groups</p>
    </div>
  );
  
  if (error) return (
    <div className="error-container">
      <FaExclamationTriangle size={48} color="#C8102E" />
      <p>Error loading tickets: {error?.message || error}</p>
    </div>
  );

  return (
    <div className="dashboard-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} canViewUserAnalytics={canViewUserAnalytics} />
      
      <div className="dashboard-content">
        {/* Dashboard Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Dashboard</h1>
            <p className="dashboard-subtitle">Manage and track all incident tickets</p>
          </div>
          <div className="dashboard-header-actions">
            <a
              href="https://ahb-av-p2pdev.ad.corp.global/pixel2purpose/home"
              className="dashboard-secondary-action external-tool-link"
              target="_blank"
              rel="noopener noreferrer"
              title="Pixel2purpose UAT (opens in a new tab)"
            >
              <span className="external-tool-badge" aria-hidden="true">NEW</span>
              <FaThLarge aria-hidden="true" />
              <span>Pixel2purpose UAT</span>
              <FaExternalLinkAlt className="external-tool-arrow" aria-hidden="true" />
            </a>
            <a
              href="https://ahb-av-nexussopd.ad.corp.global/WINGSUI/app/sopGeneration/"
              className="dashboard-secondary-action external-tool-link"
              target="_blank"
              rel="noopener noreferrer"
              title="SOP Generator UAT (opens in a new tab)"
            >
              <span className="external-tool-badge" aria-hidden="true">NEW</span>
              <FaFileAlt aria-hidden="true" />
              <span>SOP Generator UAT</span>
              <FaExternalLinkAlt className="external-tool-arrow" aria-hidden="true" />
            </a>
            <button onClick={handleChatbotToggle} className="chatbot-header-btn">
              💬 Ask AI
            </button>
            <button onClick={() => handleSyncData(ticketType)} className="sync-data-btn" disabled={loading}>
              {loading ? '⏳ Syncing...' : '🔄 Sync Data'}
            </button>
            <Link to="/analytics" className="analytics-link dashboard-secondary-action">
              <FaChartBar aria-hidden="true" />
              <span>Ticket Analytics</span>
            </Link>
            <Link to="/user-analytics" className="analytics-link dashboard-secondary-action">
              <FaUsers aria-hidden="true" />
              <span>User Analytics</span>
            </Link>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid dashboard-stats-grid">
          <div className="stat-card stat-total" onClick={() => handleStatClick('all')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaTicketAlt size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Tickets</div>
            </div>
          </div>

          <div className="stat-card stat-inprogress" onClick={() => handleStatClick('inProgress')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaPlayCircle size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.inProgress}</div>
              <div className="stat-label">In Progress</div>
            </div>
          </div>

          <div className="stat-card stat-onhold" onClick={() => handleStatClick('onHold')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaPause size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.onHold}</div>
              <div className="stat-label">On Hold</div>
            </div>
          </div>

          <div className="stat-card stat-new" onClick={() => handleStatClick('new')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaFileAlt size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.new}</div>
              <div className="stat-label">New</div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar Container */}
        <div className="search-filter-container">
          {/* Search Bar */}
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search ticket by ID, description or assignment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="search-clear"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          {/* Centralized Filter Bar */}
          <div className="filter-bar">
            <div className="filter-label">Filter By:</div>
            
            {/* STATUS Filter */}
            <div className="filter-group">
              <select
                className="dropdown-button"
                value={filters.status.length === 1 ? filters.status[0] : 'all'}
                onChange={handleStatusSelectChange}
              >
                <option value="all">All Status</option>
                {getOptions('status').map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* PRIORITY Filter */}
            <div className="filter-group">
              <select
                className="dropdown-button"
                value={filters.priority.length === 1 ? filters.priority[0] : 'all'}
                onChange={handlePrioritySelectChange}
              >
                <option value="all">All Priority</option>
                {getOptions('priority').map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* GROUP Filter */}
            <div className="filter-group group-filter-group" ref={groupDropdownRef}>
              <div className={`group-filter-input-wrap${showGroupDropdown ? ' open' : ''}`}>
                <input
                  type="text"
                  className="dropdown-button group-filter-select group-filter-input"
                  value={groupSearchValue}
                  onFocus={() => setShowGroupDropdown(true)}
                  onChange={(e) => {
                    setGroupSearchValue(e.target.value);
                    setShowGroupDropdown(true);
                  }}
                  placeholder="All Groups"
                  title={groupSearchValue || 'All Groups'}
                />
                {(selectedVisibleGroups.length > 0 || groupSearchValue) && (
                  <button
                    type="button"
                    className="group-filter-clear-btn"
                    onClick={() => {
                      setGroupSearchValue('');
                      if (selectedVisibleGroups.length > 0) {
                        handleClearGroupFilter();
                      }
                      setShowGroupDropdown(true);
                    }}
                    title="Clear group filter"
                  >
                      ×
                  </button>
                )}
                <button
                  type="button"
                  className={`group-filter-caret${showGroupDropdown ? ' open' : ''}`}
                  onClick={() => setShowGroupDropdown((prev) => !prev)}
                  title="Toggle group list"
                >
                  ▾
                </button>
              </div>

              {showGroupDropdown && (
                <div className="group-filter-dropdown">
                  <div className="group-filter-dropdown-header">Assignment Groups</div>
                  <button
                    type="button"
                    className={`group-filter-option${selectedVisibleGroups.length === 0 ? ' active' : ''}`}
                    onClick={() => {
                      handleClearGroupFilter();
                    }}
                  >
                    All Groups
                  </button>
                  {filteredGroupOptions.length > 0 ? (
                    filteredGroupOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`group-filter-option${filters.assignmentGroup.includes(option.value) ? ' active' : ''}`}
                        onClick={() => {
                          handleGroupOptionToggle(option.value);
                        }}
                      >
                        {filters.assignmentGroup.includes(option.value) ? '✓ ' : ''}{option.label}
                      </button>
                    ))
                  ) : (
                    <div className="group-filter-empty">No matching groups</div>
                  )}
                </div>
              )}
            </div>

            <div className="filter-group date-filter-group">
              <button 
                className="date-range-toggle"
                onClick={() => setShowDateRange(!showDateRange)}
                title="Filter by date range"
              >
                📅
              </button>
              {showDateRange && (
                <div className="date-range-popup">
                  <input
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => {
                      setFilters({ ...filters, dateRange: { ...filters.dateRange, start: e.target.value } });
                    }}
                    className="date-input-compact"
                  />
                  <span style={{ fontSize: '11px', color: '#718096' }}>to</span>
                  <input
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => {
                      setFilters({ ...filters, dateRange: { ...filters.dateRange, end: e.target.value } });
                      setShowDateRange(false);
                    }}
                    className="date-input-compact"
                  />
                </div>
              )}
            </div>

            <div className="filter-actions">
              <div className="configure-wrapper" ref={configureDropdownRef}>
                <button className="configure-btn" title="Configure columns" onClick={() => setShowConfigureDropdown(!showConfigureDropdown)}>
                  <FaCog size={11} /> CONFIGURE
                </button>
                {showConfigureDropdown && (
                  <div className="configure-dropdown">
                    <div className="configure-dropdown-header">Show/Hide Columns</div>
                    {Object.entries(columnLabels).map(([key, label]) => (
                      <label key={key} className="configure-dropdown-item">
                        <input
                          type="checkbox"
                          checked={columnVisibility[key]}
                          onChange={() => toggleColumnVisibility(key)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button className="export-btn" onClick={handleExportToExcel} title="Export to CSV">
                <FaDownload size={11} /> EXPORT
              </button>
            </div>
          </div>
        </div>

        {/* Tickets Table */}
        <div className="table-container">
          <div className="table-scroll">
      <table className="agent-table">
        <thead>
          <tr>
            <th className="checkbox-col">
              <input type="checkbox" />
            </th>
            {columnVisibility.ticketId && <th onClick={() => handleSort('ticketId')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Ticket ID# 
                {sortConfig.key === 'ticketId' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>}
            {columnVisibility.shortDescription && <th onClick={() => handleSort('shortDescription')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Short Description
                {sortConfig.key === 'shortDescription' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>}
            {columnVisibility.assignmentGroup && <th onClick={() => handleSort('assignmentGroup')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Assignment Group
                {sortConfig.key === 'assignmentGroup' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>}
            {columnVisibility.receivedOn && <th style={{ cursor: 'pointer', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span onClick={() => handleSort('receivedOn')}>Received On</span>
                <span
                  className="timezone-badge"
                  onClick={(e) => { e.stopPropagation(); setShowTimezoneDropdown(!showTimezoneDropdown); }}
                  title="Change timezone"
                >
                  {getTimezoneLabel()}
                </span>
                <span onClick={() => handleSort('receivedOn')}>
                  {sortConfig.key === 'receivedOn' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
                </span>
              </div>
              {showTimezoneDropdown && (
                <div className="timezone-dropdown" ref={timezoneDropdownRef}>
                  <div className="timezone-dropdown-header">Select Timezone</div>
                  {timezoneOptions.map(tz => (
                    <div
                      key={tz.value}
                      className={`timezone-dropdown-item ${selectedTimezone === tz.value ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTimezone(tz.value);
                        localStorage.setItem('agentAssistTimezone', tz.value);
                        setShowTimezoneDropdown(false);
                      }}
                    >
                      <span className="timezone-item-label">{tz.label}</span>
                      <span className="timezone-item-desc">{tz.desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </th>}
            {columnVisibility.assignedTo && <th onClick={() => handleSort('assignedTo')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Assigned To
                {sortConfig.key === 'assignedTo' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>}
            {columnVisibility.status && <th>State</th>}
            {columnVisibility.priority && <th onClick={() => handleSort('priority')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Priority
                {sortConfig.key === 'priority' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>}
            {columnVisibility.category && <th>Category</th>}
            {columnVisibility.action && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {currentTickets.map(agent => (
            <tr key={agent.ticketId} onClick={() => handleTicketClick(agent)}>
              <td className="checkbox-col">
                <input type="checkbox" onClick={(e) => e.stopPropagation()} />
              </td>
              {columnVisibility.ticketId && <td style={{ fontWeight: 600, color: '#7B3FF2' }}>{agent.ticketId}</td>}
              {columnVisibility.shortDescription && <td>{agent.shortDescription}</td>}
              {columnVisibility.assignmentGroup && <td>{agent.assignmentGroup}</td>}
              {columnVisibility.receivedOn && <td style={{ color: '#64748b', fontSize: '12px', lineHeight: '1.4' }}>
                {agent.receivedOn ? (() => {
                  const raw = typeof agent.receivedOn === 'object' ? (agent.receivedOn.display_value || agent.receivedOn.value || '') : String(agent.receivedOn);
                  if (!raw) return '';
                  // ServiceNow returns UTC as "YYYY-MM-DD HH:MM:SS" — normalize to ISO with Z
                  let normalized = raw.trim().replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})$/, '$1T$2Z');
                  // Also handle display format "MM/DD/YYYY HH:MM:SS" — convert to ISO UTC
                  const displayMatch = raw.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}:\d{2}:\d{2})$/);
                  if (displayMatch) {
                    normalized = `${displayMatch[3]}-${displayMatch[1]}-${displayMatch[2]}T${displayMatch[4]}Z`;
                  }
                  const d = new Date(normalized);
                  if (isNaN(d.getTime())) return raw; // fallback to raw string if still invalid
                  return (
                    <>
                      <div>{d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: selectedTimezone })}</div>
                      <div>{d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: selectedTimezone })}</div>
                    </>
                  );
                })() : ''}
              </td>}
              {columnVisibility.assignedTo && <td>{agent.assignedTo}</td>}
              {columnVisibility.status && <td>
                <span style={{
                  ...getStatusBadgeStyle(agent.status),
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  display: 'inline-block',
                  letterSpacing: '0.3px',
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap'
                }}>{agent.status}</span>
              </td>}
              {columnVisibility.priority && <td>
                <span style={{
                  ...getPriorityBadgeStyle(agent.priority),
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  display: 'inline-block',
                  letterSpacing: '0.3px',
                  whiteSpace: 'nowrap'
                }}>{getPriorityLabel(agent.priority)}</span>
              </td>}
              {columnVisibility.category && <td style={{ color: '#64748b', fontSize: '12px' }}>{agent.category || ''}</td>}
              {columnVisibility.action && <td>
                <button
                  className="process-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Track process button click in Azure Table Storage
                    fetch('/api/track-process-button', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        ticketNumber: agent.ticketId,
                        userName: userName,
                        userEmail: userEmail,
                        shortDescription: agent.shortDescription,
                        assignmentGroup: agent.assignmentGroup,
                        assignedTo: agent.assignedTo,
                        status: agent.status,
                        priority: agent.priority,
                        category: agent.category,
                        receivedOn: agent.receivedOn
                      })
                    }).catch(err => console.error('Failed to track process button click:', err));
                    navigate(`/process/${agent.ticketId}`);
                  }}
                >
                  <span style={{ fontSize: '11px', lineHeight: 1.2 }}>Resolution</span>
                  <span style={{ fontSize: '11px', lineHeight: 1.2 }}>Recommendation</span>
                </button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} tickets
              </div>
              <div className="pagination-buttons">
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber} className="pagination-dots">...</span>;
                  }
                  return null;
                })}
                
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="login-bottom-bar">
        <div className="bottom-bar-content">
          <img src={wiproLogo} alt="Wipro" className="bottom-bar-wipro-logo" />
          <p className="bottom-bar-copyright">Copyright © 2026 Wipro LTD. All rights reserved.</p>
        </div>
      </div>
      
      {selectedTicket && <TicketDetails ticket={selectedTicket} onClose={handleCloseDetails} />}
      
      {/* Chatbot */}
      {showChatbot && (
        <ChatbotPopup 
          onClose={() => setShowChatbot(false)}
          userEmail={userEmail}
          userName={userName}
        />
      )}
    </div>
  );
};

export default AgentAssist;